import { Fragment, useEffect, useState, useCallback } from 'react';
import { apiFetch, getToken, STREAM_API } from '../utils/streamApi';
import MediaLightbox, { popupLink } from '../components/MediaLightbox';
import AttendanceLiveView from '../components/AttendanceLiveView';
import { groupRounds, roundLabel } from '../utils/shiftRounds';

// Attendance — supervisor/admin. Today's summary tiles, a live watch panel for
// any gate, the exception queue (assign / enrol / reject), and the
// evidence-backed history report.
//
// "Exception" and PENDING_IDENTIFICATION are the same thing: somebody raised a
// hand at a gate and the face was not recognized, so the engine deliberately did
// NOT mark them present and filed the attempt here instead. Until an admin
// resolves it, that person counts as absent.

const ymd = d => d.toISOString().slice(0, 10);
const toStart = s => new Date(`${s}T00:00:00`).getTime() / 1000;
const toEnd   = s => new Date(`${s}T23:59:59`).getTime() / 1000;
const fmt = ts => { try { return new Date(ts * 1000).toLocaleString(); } catch { return '—'; } };

const STATUS = {
  VERIFIED:               { label: 'Verified',      bg: 'rgba(92,201,138,0.15)', fg: '#5cc98a' },
  ADMIN_IDENTIFIED:       { label: 'Assigned',      bg: 'rgba(77,163,255,0.15)', fg: '#4da3ff' },
  NEW_KNOWN_PERSON_CREATED: { label: 'New person',  bg: 'rgba(77,163,255,0.15)', fg: '#4da3ff' },
  CORRECTED:              { label: 'Corrected',     bg: 'rgba(77,163,255,0.15)', fg: '#4da3ff' },
  PENDING_IDENTIFICATION: { label: 'Pending ID',    bg: 'rgba(224,180,80,0.15)', fg: '#e0b450' },
  REJECTED:               { label: 'Rejected',      bg: 'rgba(224,112,112,0.12)', fg: '#e07070' },
};

export default function AttendanceReviewPage() {
  const [token, setToken]   = useState('');
  const [employees, setEmployees] = useState([]);
  const [pending, setPending] = useState([]);
  const [rows, setRows]     = useState([]);
  const [start, setStart]   = useState(ymd(new Date(Date.now() - 7 * 86400 * 1000)));
  const [end, setEnd]       = useState(ymd(new Date()));
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);   // { kind, url, title } — evidence popup
  const [summary, setSummary] = useState(null);
  const [corrections, setCorrections] = useState([]);
  const [scheds, setScheds] = useState({});       // checkpoint id/name → schedule
  const [points, setPoints] = useState([]);       // active checkpoints, for the live panel
  const [minConf, setMinConf] = useState(0.5);
  const [onboarded, setOnboarded] = useState(null);
  const [watch, setWatch]   = useState(null);     // checkpoint being watched live, or null
  const [enrolling, setEnrolling] = useState(null);
  const [enrolOn, setEnrolOn]     = useState({});  // event id → "learn this face on assign"
  const [sel, setSel]             = useState([]);  // selected exception ids, for bulk reject
  const [hSel, setHSel]           = useState([]);  // selected history ids, for bulk reject/delete
  const [pStart, setPStart]       = useState(''); // exception queue date bounds (blank = all)
  const [pEnd, setPEnd]           = useState('');
  const [fPoint, setFPoint]       = useState('');  // history filters
  const [fType, setFType]         = useState('');
  const [fStatus, setFStatus]     = useState('');
  const [fEmployee, setFEmployee] = useState('');

  const loadSummary = useCallback(async () => { const r = await apiFetch('/ui/attendance/summary'); if (r.ok) setSummary(await r.json()); }, []);
  const loadCorrections = useCallback(async () => { const r = await apiFetch('/ui/attendance/corrections'); if (r.ok) setCorrections((await r.json()).corrections || []); }, []);
  useEffect(() => { loadSummary(); loadCorrections(); }, [loadSummary, loadCorrections]);

  // Enrol the person in an exception's evidence photo and back-fill the record.
  // The evidence is a full scene frame, so the server can legitimately find no
  // usable face (subject distant or turned away) — that comes back 422 and the
  // reason is shown, rather than the old silent "success" that enrolled nothing.
  const addKnown = async (eventId) => {
    const name = window.prompt('Name for this person (enrolled as a known face):');
    if (!name || !name.trim()) return;
    const employee_id = window.prompt('Employee ID (optional):') || '';
    const department  = window.prompt('Department (optional):') || '';
    setEnrolling(eventId);
    try {
      const r = await apiFetch(`/ui/attendance/pending/${eventId}/add-known`,
                               { method: 'POST', json: { name: name.trim(), employee_id, department } });
      if (!r.ok) {
        const d = await r.json().catch(() => null);
        throw new Error(d?.detail || `Failed (${r.status})`);
      }
      await Promise.all([loadPending(), loadReport(), loadSummary()]);
    } catch (e) {
      window.alert(e.message || 'Could not add this face.');
    } finally { setEnrolling(null); }
  };
  const resolveCorrection = async (c, action) => { const r = await apiFetch(`/ui/attendance/corrections/${c.id}/resolve`, { method: 'POST', json: { action } }); if (r.ok) await loadCorrections(); };
  // Exports what you are LOOKING AT, filters included — an export that silently
  // ignores the filters hands over a different dataset than the one on screen.
  const exportCsv = () => {
    const head = ['Time', 'Employee', 'Type', 'Point', 'Status', 'Confidence'];
    const esc = x => `"${String(x ?? '').replace(/"/g, '""')}"`;
    const lines = filtered.map(r => [new Date(r.ts * 1000).toISOString(), r.person, r.event_type, r.checkpoint_name, r.identity_status, r.face_confidence].map(esc).join(','));
    const blob = new Blob([[head.join(','), ...lines].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `attendance_${start}_${end}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  useEffect(() => { getToken().then(t => setToken(t || '')); }, []);
  useEffect(() => { apiFetch('/ui/attendance/form-options').then(r => r.ok ? r.json() : null).then(d => d && setEmployees(d.employees || [])); }, []);

  // Checkpoint schedules cut the history into shifts. Read from the config, not
  // form-options: history legitimately contains shifts from checkpoints since
  // deactivated, and only the config carries their start/end.
  useEffect(() => {
    apiFetch('/ui/attendance/config').then(r => r.ok ? r.json() : null).then(d => {
      if (!d) return;
      const m = {};
      (d.checkpoints || []).forEach(cp => { if (cp.id) m[cp.id] = cp.schedule; if (cp.name) m[cp.name] = cp.schedule; });
      setScheds(m);
      setPoints((d.checkpoints || []).filter(cp => cp.active !== false));
      if (d.face_min_conf != null) setMinConf(Number(d.face_min_conf));
      // Empty directory = the engine's "any known face counts" fallback, so
      // pass null rather than an empty list, which would gate everything.
      const emps = d.employees || [];
      setOnboarded(emps.length ? emps.filter(e => e.onboarded_at).map(e => e.name) : null);
    }).catch(() => {});
  }, []);

  const evUrl = f => f ? `${STREAM_API}/ui/attendance/evidence/${encodeURIComponent(f)}${token ? `?token=${token}` : ''}` : null;
  const evTitle = (o, who) => [who, o.checkpoint_name, o.event_type, fmt(o.ts)].filter(Boolean).join(' · ');

  const loadPending = useCallback(async () => {
    const r = await apiFetch('/ui/attendance/pending');
    if (r.ok) setPending((await r.json()).pending || []);
  }, []);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`/ui/attendance/report?start=${toStart(start)}&end=${toEnd(end)}`);
      const d = r.ok ? await r.json() : { events: [] };
      setRows((d.events || []).sort((a, b) => b.ts - a.ts));
    } finally { setLoading(false); }
  }, [start, end]);

  useEffect(() => { loadPending(); loadReport(); }, [loadPending, loadReport]);

  // Filter options come from the loaded rows, not the config: history
  // legitimately contains points and people since removed, and an option list
  // built from the config alone would make those rows unreachable.
  const pointOpts  = [...new Set(rows.map(r => r.checkpoint_name).filter(Boolean))].sort();
  const typeOpts   = [...new Set(rows.map(r => r.event_type).filter(Boolean))].sort();
  const statusOpts = [...new Set(rows.map(r => r.identity_status).filter(Boolean))].sort();
  const empOpts    = [...new Set(rows.map(r => r.person).filter(Boolean))].sort();
  const filtered = rows.filter(r =>
    (!fPoint    || r.checkpoint_name  === fPoint) &&
    (!fType     || r.event_type       === fType) &&
    (!fStatus   || r.identity_status  === fStatus) &&
    (!fEmployee || r.person           === fEmployee));
  const anyFilter = fPoint || fType || fStatus || fEmployee;

  // One block per shift, cut on the checkpoint's own schedule start/end so a
  // night shift stays whole instead of being split at midnight.
  const groups = groupRounds(filtered, r => r.ts,
    r => ({ key: r.checkpoint_id || r.checkpoint_name, label: r.checkpoint_name,
            sched: scheds[r.checkpoint_id] || scheds[r.checkpoint_name] }),
    '08:30', '19:00');

  // `enrol` adds this capture to that employee's known faces so the gate starts
  // recognising them. Opt-in per row: the evidence is a full scene frame, so an
  // automatic enrol on every assignment would eventually file the wrong face
  // under someone's name with nothing downstream to reveal it.
  const assign = async (ev, person) => {
    if (!person) return;
    const enrol = !!enrolOn[ev.id];
    const r = await apiFetch(`/ui/attendance/pending/${ev.id}/assign`, { method: 'POST', json: { person, enrol } });
    if (!r.ok) { window.alert('Could not assign this record.'); return; }
    const d = await r.json().catch(() => null);
    // The credit succeeded even when the face could not be read — say which
    // happened rather than letting a silent no-op look like a successful enrol.
    if (enrol && d && !d.enrolled) {
      window.alert(`Assigned to ${person}, but the face was not enrolled — ${d.enrol_error || 'no usable face in the photo'}.`);
    }
    setEnrolOn(m => ({ ...m, [ev.id]: false }));
    await Promise.all([loadPending(), loadReport(), loadSummary()]);
  };
  const reject = async ev => {
    if (!window.confirm('Reject this attendance attempt?')) return;
    const r = await apiFetch(`/ui/attendance/pending/${ev.id}/reject`, { method: 'POST', json: { reason: 'not an employee attempt' } });
    if (r.ok) { setSel(s => s.filter(x => x !== ev.id)); await loadPending(); await loadReport(); }
  };

  // Exception queue is deliberately NOT bound to the history date range — an
  // unresolved exception must not vanish because you were looking at last week.
  // These bounds are an optional narrowing on top, blank by default.
  const shownPending = pending.filter(p =>
    (!pStart || p.ts >= toStart(pStart)) && (!pEnd || p.ts <= toEnd(pEnd)));

  const toggleSel = id => setSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const allSelected = shownPending.length > 0 && shownPending.every(p => sel.includes(p.id));
  const toggleSelAll = () => setSel(allSelected ? [] : shownPending.map(p => p.id));

  // ── History row actions ──
  const toggleHSel = id => setHSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const allHSelected = filtered.length > 0 && filtered.every(r => r.id && hSel.includes(r.id));
  const toggleHSelAll = () => setHSel(allHSelected ? [] : filtered.map(r => r.id).filter(Boolean));

  const rejectHistory = async ids => {
    if (!ids.length) return;
    if (!window.confirm(`Reject ${ids.length} record${ids.length === 1 ? '' : 's'}? They stay in the log, marked Rejected.`)) return;
    const r = await apiFetch('/ui/attendance/pending/reject-bulk',
                             { method: 'POST', json: { event_ids: ids, reason: 'rejected from review' } });
    if (!r.ok) { window.alert('Reject failed.'); return; }
    const d = await r.json().catch(() => null);
    // Only PENDING rows can be rejected — say so rather than letting a no-op
    // look like it worked when the selection was already-credited punches.
    if (d && d.rejected === 0) window.alert('Nothing was rejected — only unresolved exceptions can be rejected.');
    setHSel([]);
    await Promise.all([loadPending(), loadReport(), loadSummary()]);
  };

  const deleteHistory = async ids => {
    if (!ids.length) return;
    if (!window.confirm(`Permanently delete ${ids.length} record${ids.length === 1 ? '' : 's'}, including the photo and clip?\n\nThis cannot be undone. Who deleted what is kept in the audit log.`)) return;
    const r = await apiFetch('/ui/attendance/events/delete',
                             { method: 'POST', json: { event_ids: ids, reason: 'deleted from review' } });
    if (!r.ok) { window.alert('Delete failed.'); return; }
    setHSel([]); setSel(s => s.filter(x => !ids.includes(x)));
    await Promise.all([loadPending(), loadReport(), loadSummary()]);
  };

  // One request, one rewrite of the events log — rejecting a backlog row by row
  // would rewrite the whole file once per row.
  const rejectSelected = async () => {
    if (!sel.length) return;
    if (!window.confirm(`Reject ${sel.length} attendance attempt${sel.length === 1 ? '' : 's'}? This cannot be undone.`)) return;
    const r = await apiFetch('/ui/attendance/pending/reject-bulk',
                             { method: 'POST', json: { event_ids: sel, reason: 'bulk reject' } });
    if (!r.ok) { window.alert('Bulk reject failed.'); return; }
    setSel([]);
    await Promise.all([loadPending(), loadReport(), loadSummary()]);
  };

  return (
    <div style={{ padding: 24, maxWidth: 1000, margin: '0 auto', color: 'var(--text)' }}>
      <h1 style={{ margin: '0 0 4px', fontSize: 24 }}>🕘 Attendance — Review</h1>
      <p style={{ color: 'var(--text-muted)', marginTop: 0 }}>Today's summary, live gates, and the exception queue.</p>

      {summary && (<>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
          {[['Present', summary.counts.present, '#5cc98a'],
            ['Late', summary.counts.late, '#e0b450'],
            // Before the cut-off nobody has failed to turn up yet, so the tile
            // shows Awaited instead of calling the whole roster absent at 08:00.
            ...(summary.past_cutoff
                ? [['Absent', summary.counts.absent, '#e07070']]
                : [['Awaited', summary.counts.awaited ?? 0, '#8a94a6']]),
            ['Missing out', summary.counts.missing_checkout, '#e0b450'],
            ['Exceptions', summary.counts.pending, '#4da3ff'],
            ['Corrections', summary.pending_corrections || 0, '#4da3ff'],
            ...(summary.counts.not_onboarded ? [['Not onboarded', summary.counts.not_onboarded, '#e0b450']] : []),
          ].map(([l, v, c]) => (
            <div key={l} style={{ ...card, marginTop: 0, minWidth: 104, textAlign: 'center', padding: 14 }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: c }}>{v}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{l}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
          {summary.date}
          {summary.absent_after && (summary.past_cutoff
            ? ` · absent declared after ${summary.absent_after}`
            : ` · no-shows become Absent at ${summary.absent_after}`)}
        </div>
      </>)}

      {/* ── Live gates ── */}
      {points.length > 0 && (
        <div style={{ ...card }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <b style={{ fontSize: 16 }}>Live gates</b>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Watching runs pose on that camera — open one at a time.</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
            {points.map(cp => (
              <div key={cp.id} style={cpChip}>
                <span>{cp.name}</span>
                <span style={{ ...badge, background: 'rgba(120,120,120,0.16)', color: 'var(--text-muted)' }}>
                  {cp.purpose === 'checkout' ? 'exit' : 'entry'}
                </span>
                <button onClick={() => setWatch(w => w?.id === cp.id ? null : cp)} style={watchBtn}>
                  {watch?.id === cp.id ? '■ Stop' : '▶ Watch'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {watch && (
        <div style={{ ...card, background: '#0b0b0b' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <b>Live — {watch.name}</b>
            <button onClick={() => setWatch(null)} style={watchBtn}>Close</button>
          </div>
          <AttendanceLiveView cameraIdx={watch.camera_idx} checkpointId={watch.id}
                              polygon={(watch.zones && watch.zones[0]) || []}
                              minConf={minConf} holdSeconds={watch.gesture_hold_seconds || 1.5}
                              onboarded={onboarded} />
        </div>
      )}

      {/* Exception queue — unrecognised attempts, filed by a raised hand */}
      <h2 style={{ fontSize: 18, marginTop: 16 }}>Exceptions {pending.length > 0 && <span style={{ color: 'var(--warning)' }}>({pending.length})</span>}</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: '0 0 4px' }}>
        Someone raised a hand at a gate but was not recognised. Nothing was marked — assign, enrol, or reject.
      </p>
      {/* Same grid shape as the history table below, with the evidence behind
          Photo/Clip links rather than 14 full-width thumbnails to scroll past. */}
      {pending.length > 0 && (
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 10 }}>
          <div><div style={lbl}>From</div><input type="date" value={pStart} onChange={e => setPStart(e.target.value)} style={input} /></div>
          <div><div style={lbl}>To</div><input type="date" value={pEnd} onChange={e => setPEnd(e.target.value)} style={input} /></div>
          {(pStart || pEnd) && <button onClick={() => { setPStart(''); setPEnd(''); }} style={btn}>All dates</button>}
          <button onClick={toggleSelAll} style={btn}>{allSelected ? 'Clear selection' : `Select all (${shownPending.length})`}</button>
          <button onClick={rejectSelected} disabled={!sel.length}
                  style={{ ...btn, color: sel.length ? 'var(--danger)' : 'var(--text-muted)', opacity: sel.length ? 1 : 0.6 }}>
            Reject selected{sel.length ? ` (${sel.length})` : ''}
          </button>
          <button onClick={() => deleteHistory(sel)} disabled={!sel.length}
                  style={{ ...btn, color: sel.length ? 'var(--danger)' : 'var(--text-muted)', opacity: sel.length ? 1 : 0.6 }}>
            Delete selected{sel.length ? ` (${sel.length})` : ''}
          </button>
          <div style={{ color: 'var(--text-muted)', fontSize: 13, alignSelf: 'center' }}>
            {sel.length > 0 ? `${sel.length} of ${shownPending.length} selected`
              : (pStart || pEnd) ? `${shownPending.length} of ${pending.length} shown` : ''}
          </div>
        </div>
      )}
      {shownPending.length === 0
        ? <div style={{ ...card, color: 'var(--text-muted)' }}>
            {pending.length ? 'No exceptions in this date range.' : 'Nothing pending. 🎉'}
          </div>
        : <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 10, marginTop: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 760 }}>
              <thead><tr style={{ textAlign: 'left', color: 'var(--text-muted)', background: 'var(--surface)' }}>
                <th style={{ padding: '10px 12px', width: 34 }}>
                  <input type="checkbox" checked={allSelected} onChange={toggleSelAll}
                         title={allSelected ? 'Clear selection' : 'Select all'} />
                </th>
                {['Time', 'Point', 'Type', 'Proof', 'Assign to', ''].map((h, i) =>
                  <th key={i} style={{ padding: '10px 12px', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {shownPending.map(ev => (
                  <tr key={ev.id} style={{ borderTop: '1px solid var(--border)',
                                           background: sel.includes(ev.id) ? 'var(--primary-muted)' : 'transparent' }}>
                    <td style={td}>
                      <input type="checkbox" checked={sel.includes(ev.id)} onChange={() => toggleSel(ev.id)} />
                    </td>
                    <td style={td}>{fmt(ev.ts)}</td>
                    <td style={td}>{ev.checkpoint_name}</td>
                    <td style={td}>{ev.event_type}</td>
                    <td style={td}>
                      {ev.evidence_image && <button type="button" style={popupLink(link)}
                        onClick={() => setPreview({ kind: 'image', url: evUrl(ev.evidence_image), title: evTitle(ev) })}>Photo</button>}
                      {ev.evidence_video && <button type="button" style={{ ...popupLink(link), marginLeft: 10 }}
                        onClick={() => setPreview({ kind: 'video', url: evUrl(ev.evidence_video), title: evTitle(ev) })}>Clip</button>}
                      {!ev.evidence_image && !ev.evidence_video && <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td style={td}>
                      <select defaultValue="" onChange={e => assign(ev, e.target.value)} style={{ ...input, minWidth: 170 }}>
                        <option value="">Assign to employee…</option>
                        {employees.map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer' }}
                             title="Adds this photo to that employee's known faces so the gate starts recognising them. Only tick it if the photo clearly shows the right person.">
                        <input type="checkbox" checked={!!enrolOn[ev.id]}
                               onChange={e => setEnrolOn(m => ({ ...m, [ev.id]: e.target.checked }))} />
                        Learn this face
                      </label>
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <button onClick={() => addKnown(ev.id)} disabled={enrolling === ev.id} style={btn}>
                        {enrolling === ev.id ? 'Adding…' : 'Add new'}
                      </button>
                      <button onClick={() => reject(ev)} style={{ ...btn, color: 'var(--danger)', marginLeft: 8 }}>Reject</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}

      {corrections.filter(c => c.status === 'OPEN').length > 0 && (<>
        <h2 style={{ fontSize: 18, marginTop: 28 }}>Correction Requests ({corrections.filter(c => c.status === 'OPEN').length})</h2>
        <div style={card}>
          {corrections.filter(c => c.status === 'OPEN').map(c => (
            <div key={c.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
              <b>{c.name}</b>
              <span style={{ color: 'var(--text-muted)' }}>{c.message}</span>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                <button onClick={() => resolveCorrection(c, 'resolve')} style={{ ...btn, color: '#5cc98a' }}>Approve</button>
                <button onClick={() => resolveCorrection(c, 'reject')} style={{ ...btn, color: 'var(--danger)' }}>Reject</button>
              </div>
            </div>
          ))}
        </div>
      </>)}

      {/* History */}
      <h2 style={{ fontSize: 18, marginTop: 28 }}>History</h2>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', margin: '12px 0' }}>
        <div><div style={lbl}>From</div><input type="date" value={start} onChange={e => setStart(e.target.value)} style={input} /></div>
        <div><div style={lbl}>To</div><input type="date" value={end} onChange={e => setEnd(e.target.value)} style={input} /></div>
        <button onClick={loadReport} style={btn}>{loading ? 'Loading…' : 'Refresh'}</button>
        <div><div style={lbl}>Point</div>
          <select value={fPoint} onChange={e => setFPoint(e.target.value)} style={input}>
            <option value="">All points</option>{pointOpts.map(o => <option key={o} value={o}>{o}</option>)}
          </select></div>
        <div><div style={lbl}>Type</div>
          <select value={fType} onChange={e => setFType(e.target.value)} style={input}>
            <option value="">All types</option>{typeOpts.map(o => <option key={o} value={o}>{o}</option>)}
          </select></div>
        <div><div style={lbl}>Status</div>
          <select value={fStatus} onChange={e => setFStatus(e.target.value)} style={input}>
            <option value="">All statuses</option>
            {statusOpts.map(o => <option key={o} value={o}>{STATUS[o]?.label || o}</option>)}
          </select></div>
        <div><div style={lbl}>Employee</div>
          <select value={fEmployee} onChange={e => setFEmployee(e.target.value)} style={input}>
            <option value="">All employees</option>{empOpts.map(o => <option key={o} value={o}>{o}</option>)}
          </select></div>
        {anyFilter && <button onClick={() => { setFPoint(''); setFType(''); setFStatus(''); setFEmployee(''); }} style={btn}>Clear</button>}
        <button onClick={exportCsv} disabled={!filtered.length} style={btn}>Export CSV</button>
        <button onClick={() => rejectHistory(hSel)} disabled={!hSel.length}
                style={{ ...btn, color: hSel.length ? 'var(--danger)' : 'var(--text-muted)', opacity: hSel.length ? 1 : 0.6 }}>
          Reject{hSel.length ? ` (${hSel.length})` : ''}
        </button>
        <button onClick={() => deleteHistory(hSel)} disabled={!hSel.length}
                style={{ ...btn, color: hSel.length ? 'var(--danger)' : 'var(--text-muted)', opacity: hSel.length ? 1 : 0.6 }}>
          Delete{hSel.length ? ` (${hSel.length})` : ''}
        </button>
        <div style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: 13, alignSelf: 'center' }}>
          {filtered.length} record{filtered.length === 1 ? '' : 's'}
          {anyFilter && rows.length !== filtered.length && <span> of {rows.length}</span>}
        </div>
      </div>
      <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 720 }}>
          <thead><tr style={{ textAlign: 'left', color: 'var(--text-muted)', background: 'var(--surface)' }}>
            <th style={{ padding: '10px 12px', width: 34 }}>
              <input type="checkbox" checked={allHSelected} onChange={toggleHSelAll}
                     title={allHSelected ? 'Clear selection' : 'Select all shown'} />
            </th>
            {['Time', 'Employee', 'Type', 'Point', 'Status', 'Proof', ''].map((h, i) => <th key={i} style={{ padding: '10px 12px', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
              {loading ? 'Loading…' : (rows.length ? 'No records match the filters.' : 'No records in this range.')}</td></tr>}
            {groups.map(g => {
              const ins  = g.rows.filter(r => r.event_type === 'checkin').length;
              const outs = g.rows.filter(r => r.event_type === 'checkout').length;
              return (
            <Fragment key={g.key}>
                <tr>
                  <td colSpan={8} style={roundCell}>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'baseline' }}>
                      <b>{g.owner}</b>
                      <span>{roundLabel(g)}</span>
                      <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
                        {g.sched?.start || '08:30'}–{g.sched?.end || '19:00'}
                      </span>
                      <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontWeight: 400 }}>
                        {ins} in · {outs} out
                      </span>
                    </div>
                  </td>
                </tr>
                {g.rows.map((r, i) => {
              const s = STATUS[r.identity_status] || STATUS.PENDING_IDENTIFICATION;
              return (
                <tr key={i} style={{ borderTop: '1px solid var(--border)',
                                     background: r.id && hSel.includes(r.id) ? 'var(--primary-muted)' : 'transparent' }}>
                  <td style={td}>
                    {r.id && <input type="checkbox" checked={hSel.includes(r.id)} onChange={() => toggleHSel(r.id)} />}
                  </td>
                  <td style={td}>{fmt(r.ts)}</td>
                  <td style={td}>
                    {r.person || <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    {/* An unresolved exception in history is still enrollable —
                        the same offer the patrol report makes, so a record does
                        not become a dead end once it scrolls out of the queue. */}
                    {!r.person && r.id && r.evidence_image && r.identity_status !== 'REJECTED' && (
                      <button type="button" style={{ ...popupLink(link), marginLeft: 8 }}
                        disabled={enrolling === r.id}
                        onClick={() => addKnown(r.id)}>
                        {enrolling === r.id ? 'Adding…' : '+ Add as known'}
                      </button>
                    )}
                  </td>
                  <td style={td}>{r.event_type}</td>
                  <td style={td}>{r.checkpoint_name}</td>
                  <td style={td}><span style={{ ...badge, background: s.bg, color: s.fg }}>{s.label}</span></td>
                  <td style={td}>
                    {r.evidence_image && <button type="button" style={popupLink(link)}
                      onClick={() => setPreview({ kind: 'image', url: evUrl(r.evidence_image), title: evTitle(r, r.person) })}>Photo</button>}
                    {r.evidence_video && <button type="button" style={{ ...popupLink(link), marginLeft: 10 }}
                      onClick={() => setPreview({ kind: 'video', url: evUrl(r.evidence_video), title: evTitle(r, r.person) })}>Clip</button>}
                    {!r.evidence_image && !r.evidence_video && <span style={{ color: 'var(--text-muted)' }}>—</span>}
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    {/* Reject is only meaningful on an unresolved exception; a
                        credited punch is corrected or deleted, never "rejected". */}
                    {r.id && r.identity_status === 'PENDING_IDENTIFICATION' &&
                      <button type="button" style={{ ...popupLink(link), color: 'var(--danger)' }}
                              onClick={() => rejectHistory([r.id])}>Reject</button>}
                    {r.id && <button type="button" style={{ ...popupLink(link), color: 'var(--danger)', marginLeft: 10 }}
                                     onClick={() => deleteHistory([r.id])}>Delete</button>}
                  </td>
                </tr>
              );
                })}
            </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <MediaLightbox media={preview} onClose={() => setPreview(null)} />
    </div>
  );
}

const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginTop: 12 };
const input = { padding: '8px 10px', background: 'var(--surface-alt)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14 };
const btn = { padding: '7px 12px', background: 'var(--surface-alt)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', fontSize: 13 };
const lbl = { fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 };
const td = { padding: '10px 12px', whiteSpace: 'nowrap' };
const roundCell = { padding: '8px 12px', fontSize: 13, background: 'var(--surface)',
                    borderTop: '2px solid var(--border)', color: 'var(--text)' };
const badge = { padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 };
const link = { color: 'var(--accent)', textDecoration: 'underline', cursor: 'pointer' };
const cpChip = { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface-alt)' };
const watchBtn = { padding: '4px 10px', background: 'transparent', color: 'var(--accent)', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', fontSize: 12 };
